import React, { useState, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { cn } from 'lib/utils';

export interface ImageFile {
    id: string;
    file?: File;
    url?: string;
    name: string;
    isMain?: boolean;
    preview?: string;
}

interface ImageUploaderProps {
    images: ImageFile[];
    onChange: (images: ImageFile[]) => void;
    maxImages?: number;
    accept?: string;
    multiple?: boolean;
    /** "대표" 배지/설정 버튼 노출 여부. 미지정 시 maxImages === 1 이면 자동 숨김 */
    showMainBadge?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
    images,
    onChange,
    maxImages = 10,
    accept = 'image/*',
    multiple = true,
    showMainBadge,
}) => {
    // 이미지가 1장만 허용되면 "대표" 기능 무의미 → 자동으로 숨김
    const shouldShowMain = showMainBadge ?? maxImages > 1;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        processFiles(files);
    };

    const processFiles = (files: File[]) => {
        const remainingSlots = maxImages - images.length;
        const filesToAdd = files.slice(0, remainingSlots);

        const newImages: ImageFile[] = filesToAdd.map(file => ({
            id: `${Date.now()}-${Math.random()}`,
            file,
            name: file.name,
            preview: URL.createObjectURL(file),
            isMain: images.length === 0 && filesToAdd[0] === file // First image is main by default
        }));

        onChange([...images, ...newImages]);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);

        const files = Array.from(e.dataTransfer.files).filter(file =>
            file.type.startsWith('image/')
        );
        processFiles(files);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(images);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        onChange(items);
    };

    const removeImage = (id: string) => {
        const updatedImages = images.filter(img => img.id !== id);

        // If removed image was main, set first image as main
        if (images.find(img => img.id === id)?.isMain && updatedImages.length > 0) {
            updatedImages[0].isMain = true;
        }

        onChange(updatedImages);
    };

    const setMainImage = (id: string) => {
        const updatedImages = images.map(img => ({
            ...img,
            isMain: img.id === id
        }));
        onChange(updatedImages);
    };

    return (
        <div className="w-full">
            {/* Upload Area */}
            {images.length < maxImages && (
                <div
                    className={cn(
                        'cursor-pointer rounded border border-dashed px-5 py-8 text-center transition-colors duration-fast md:py-10',
                        images.length > 0 && 'mb-4',
                        dragOver
                            ? 'border-navy-700 bg-navy-50'
                            : 'border-neutral-300 bg-neutral-50 hover:border-neutral-400 hover:bg-neutral-100'
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        multiple={multiple}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <div className="flex flex-col items-center gap-2">
                        <svg className="h-9 w-9 text-neutral-400 md:h-10 md:w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-md font-medium text-neutral-700">
                            클릭하거나 이미지를 드래그하여 업로드
                        </p>
                        <p className="text-sm text-muted-foreground">
                            최대 {maxImages}개 이미지 (현재 {images.length}개)
                        </p>
                    </div>
                </div>
            )}

            {/* Image Preview Grid */}
            {images.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="images" direction="horizontal">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3 p-1 md:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] md:gap-4"
                            >
                                {images.map((image, index) => (
                                    <Draggable key={image.id} draggableId={image.id} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={cn(
                                                    'group relative overflow-hidden rounded border bg-card transition-colors duration-fast',
                                                    image.isMain && shouldShowMain
                                                        ? 'border-primary'
                                                        : 'border-border hover:border-neutral-300',
                                                    snapshot.isDragging && 'opacity-50'
                                                )}
                                            >
                                                <div className="relative w-full pt-[100%]">
                                                    {/* Drag Handle */}
                                                    <div
                                                        {...provided.dragHandleProps}
                                                        className="absolute left-2 top-2 z-10 cursor-grab rounded bg-white/90 p-1.5 opacity-0 transition-opacity duration-fast active:cursor-grabbing group-hover:opacity-100"
                                                    >
                                                        <svg className="h-4 w-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                d="M4 6h16M4 12h16M4 18h16" />
                                                        </svg>
                                                    </div>

                                                    {/* Main Badge */}
                                                    {shouldShowMain && image.isMain && (
                                                        <div className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground">
                                                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                            </svg>
                                                            대표
                                                        </div>
                                                    )}

                                                    {/* Image */}
                                                    <img
                                                        src={image.preview || image.url || ''}
                                                        alt={image.name}
                                                        className="absolute inset-0 h-full w-full object-cover"
                                                    />

                                                    {/* Image Info */}
                                                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/60 p-2 text-white opacity-0 transition-opacity duration-fast group-hover:opacity-100">
                                                        <span className="max-w-[70%] truncate text-sm font-medium">{image.name}</span>
                                                        <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] tabular-nums">{index + 1}</span>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="absolute left-1/2 top-1/2 z-[15] flex -translate-x-1/2 -translate-y-1/2 gap-2 opacity-0 transition-opacity duration-fast group-hover:opacity-100">
                                                        {shouldShowMain && !image.isMain && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setMainImage(image.id)}
                                                                className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-primary transition-colors duration-fast hover:bg-navy-50"
                                                                title="대표 이미지로 설정"
                                                            >
                                                                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(image.id)}
                                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive text-destructive-foreground transition-colors duration-fast hover:bg-destructive/90"
                                                            title="삭제"
                                                        >
                                                            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                    d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </div>
    );
};

export default ImageUploader;
